import React, { useEffect, useState, useCallback } from "react";
import { api, tokenStore, API } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Download, Search, FileText, RefreshCw } from "lucide-react";
import { cn } from "../lib/utils";

const QUICK_RANGES = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "this_week", label: "This week" },
  { key: "last_week", label: "Last week" },
  { key: "last_30", label: "Last 30 days" },
];

function computeRange(key) {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  if (key === "today") { start.setHours(0, 0, 0, 0); }
  else if (key === "yesterday") { start.setDate(start.getDate() - 1); start.setHours(0, 0, 0, 0); end.setDate(end.getDate() - 1); end.setHours(23, 59, 59, 999); }
  else if (key === "this_week") { const dow = start.getDay() || 7; start.setDate(start.getDate() - (dow - 1)); start.setHours(0, 0, 0, 0); }
  else if (key === "last_week") { const dow = start.getDay() || 7; start.setDate(start.getDate() - (dow - 1) - 7); start.setHours(0, 0, 0, 0); end.setDate(end.getDate() - dow); end.setHours(23, 59, 59, 999); }
  else if (key === "last_30") { start.setDate(start.getDate() - 30); start.setHours(0, 0, 0, 0); }
  return { from: start.toISOString(), to: end.toISOString() };
}

const CATEGORY_STYLE = {
  Login: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  Auth: "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30",
  Settings: "bg-primary/10 text-primary border-primary/30",
  Admin: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
  App: "bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30",
};

// Always render timestamps in Mountain (Edmonton) time — America/Edmonton observes MDT in
// summer and MST in winter automatically.
const MDT_TZ = "America/Edmonton";
const MDT_DATE_FMT = new Intl.DateTimeFormat("en-CA", {
  timeZone: MDT_TZ,
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
});
function fmtMDT(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return MDT_DATE_FMT.format(d).replace(",", " ·");
}

export default function ActivityLogs() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [range, setRange] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (q.trim()) params.q = q.trim();
      if (category && category !== "all") params.category = category;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo).toISOString();
      const { data } = await api.get("/activity-logs", { params });
      setItems(data.items || []);
      setCategories(data.categories || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  }, [q, category, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const applyQuick = (key) => {
    setRange(key);
    const r = computeRange(key);
    setDateFrom(r.from.slice(0, 16));
    setDateTo(r.to.slice(0, 16));
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (category && category !== "all") params.set("category", category);
    if (dateFrom) params.set("date_from", new Date(dateFrom).toISOString());
    if (dateTo) params.set("date_to", new Date(dateTo).toISOString());
    // Use fetch with auth then blob download to include Authorization header
    fetch(`${API}/activity-logs/export.csv?${params.toString()}`, {
      headers: { Authorization: `Bearer ${tokenStore.get()}` },
    })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url;
        a.download = `activity-logs-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click(); URL.revokeObjectURL(url);
      });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-primary text-[12px] font-semibold uppercase tracking-wider"><FileText className="w-3.5 h-3.5" /> Activity</div>
          <h1 className="text-[26px] font-semibold tracking-tight mt-1">Activity Logs</h1>
          <p className="text-[13px] text-muted-foreground">Login history, user changes, permission updates and app access. {total.toLocaleString()} record(s).</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5"><RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} /> Refresh</Button>
          <Button size="sm" onClick={exportCsv} className="gap-1.5"><Download className="w-4 h-4" /> Export CSV</Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search actions, actors…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
          <div className="md:col-span-2">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue placeholder="All categories" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-muted-foreground">Quick range</span>
          {QUICK_RANGES.map((r) => (
            <button key={r.key} onClick={() => applyQuick(r.key)} className={cn("h-7 px-2.5 rounded-full border text-[12px]", range === r.key ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground")}>
              {r.label}
            </button>
          ))}
          <span className="text-muted-foreground text-[12px] mx-1">|</span>
          <Input type="datetime-local" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setRange(null); }} className="h-8 w-56" />
          <span className="text-[12px] text-muted-foreground">to</span>
          <Input type="datetime-local" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setRange(null); }} className="h-8 w-56" />
          {(dateFrom || dateTo || q || (category && category !== "all")) && (
            <Button variant="ghost" size="sm" onClick={() => { setQ(""); setCategory("all"); setDateFrom(""); setDateTo(""); setRange(null); }} className="text-muted-foreground">Clear</Button>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground border-b border-border bg-muted/40">
          <div className="col-span-4">Action</div>
          <div className="col-span-2">Actor</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-3">Details</div>
          <div className="col-span-1 text-right">Time (MDT)</div>
        </div>
        <div className="divide-y divide-border tabler-scroll max-h-[62vh] overflow-y-auto">
          {loading ? (
            [...Array(8)].map((_, i) => <div key={i} className="h-12 bg-muted/30 animate-pulse" />)
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground text-[13px]">No matching activity.</div>
          ) : items.map((it) => (
            <div key={it.id} className="grid grid-cols-12 gap-3 items-center px-4 py-2.5 hover:bg-muted/30 text-[13px]">
              <div className="col-span-4 flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-muted flex items-center justify-center text-muted-foreground"><FileText className="w-3.5 h-3.5" /></div>
                <span className="font-medium text-foreground truncate" title={it.action}>{it.action}</span>
              </div>
              <div className="col-span-2 text-foreground truncate" title={it.actor_email}>{it.actor_name || it.actor_email}</div>
              <div className="col-span-2">
                <span className={cn("inline-flex items-center rounded-full border text-[10.5px] font-semibold px-2 py-0.5 uppercase tracking-wide", CATEGORY_STYLE[it.category] || "bg-muted text-foreground border-border")}>{it.category || "—"}</span>
              </div>
              <div className="col-span-3 text-muted-foreground truncate" title={it.details}>{it.details || "—"}</div>
              <div className="col-span-1 text-right text-muted-foreground text-[12px]" title={it.ts}>
                {fmtMDT(it.ts)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
